import React, { useMemo, useState } from 'react'
import { isEmpty, isNil } from 'lodash'
import { IconCpu, IconThumbDown } from '@tabler/icons-react'

import { CopyButton } from '@/components/buttons/CopyButton'
import { Pill } from '@/components/pills/Pill'
import { Accordion } from '@/components/accordions/Accordion'
import { formatTime } from '@/utils/helpers'

import { formatCost, formatMs, formatNumber } from './format'

// The rendered audit row: actor/model card, timing, feedback, tool calls,
// skills, prompt, and a collapsible response.
export const AuditRecord = ({ record }) => {
  const toolCalls = record.tool_calls || []
  const skills = record.skills_used || []
  const [openSteps, setOpenSteps] = useState([])
  const [responseOpen, setResponseOpen] = useState(false)

  // The copy payload is needed for every step's CopyButton even while collapsed,
  // so serialize once per record rather than re-stringifying all steps on each
  // accordion toggle.
  const serializedToolCalls = useMemo(
    () => toolCalls.map((tool) => JSON.stringify(tool, null, 2)),
    [toolCalls]
  )

  const toggleStep = (index) =>
    setOpenSteps((current) =>
      current.includes(index) ? current.filter((item) => item !== index) : [...current, index]
    )

  return (
    <div className="kodi-audit__body">
      <div className="kodi-audit__card">
        <div className="kodi-audit__actor">
          <span className="kodi-audit__actor-name">{record.actor || 'Unknown user'}</span>
          <span className="muted-text small-text kodi-audit__actor-time">
            {formatTime(record.created_at)}
          </span>
        </div>
        <div className="kodi-audit__actor-meta muted-text small-text">
          user {record.user_id} · org {record.org_id}
        </div>
        <div className="kodi-audit__model">
          <IconCpu size={14} />
          {record.model} <span className="muted-text">· {record.provider}</span>
        </div>
        <div className="kodi-audit__summary">
          <div>
            <span className="kodi-audit__k">Input tokens</span>
            <span className="kodi-audit__v">{formatNumber(record.input_tokens_used)}</span>
          </div>
          <div>
            <span className="kodi-audit__k">Output tokens</span>
            <span className="kodi-audit__v">{formatNumber(record.output_tokens_used)}</span>
          </div>
          <div>
            <span className="kodi-audit__k">Cost</span>
            <span className="kodi-audit__v">{formatCost(record.total_cost)}</span>
          </div>
        </div>
      </div>

      <div>
        <p className="kodi-audit__label">Timing</p>
        <div className="kodi-audit__metrics">
          <div>
            <span className="kodi-audit__k">First token</span>
            <span className="kodi-audit__v">{formatMs(record.time_to_first_token_ms)}</span>
          </div>
          <div>
            <span className="kodi-audit__k">First answer</span>
            <span className="kodi-audit__v">{formatMs(record.time_to_first_answer_ms)}</span>
          </div>
          <div>
            <span className="kodi-audit__k">Duration</span>
            <span className="kodi-audit__v">{formatMs(record.total_duration_ms)}</span>
          </div>
        </div>
      </div>

      {record.feedback_type === 'negative' && (
        <div>
          <p className="kodi-audit__label">User feedback</p>
          <div className="kodi-audit__feedback">
            <strong className="kodi-audit__feedback-title">
              <IconThumbDown size={14} /> Negative
            </strong>
            {!isEmpty(record.feedback_categories) && (
              <div className="kodi-audit__skills">
                {record.feedback_categories.map((category) => (
                  <Pill key={category} xs>
                    {category}
                  </Pill>
                ))}
              </div>
            )}
            {record.feedback_comment && (
              <p className="kodi-audit__feedback-comment muted-text small-text">
                {record.feedback_comment}
              </p>
            )}
          </div>
        </div>
      )}

      {!isEmpty(toolCalls) && (
        <div>
          <p className="kodi-audit__label">
            What Kodi did · {toolCalls.length} tool call{toolCalls.length > 1 ? 's' : ''}
          </p>
          <div className="kodi-audit__steps">
            {toolCalls.map((tool, index) => (
              <Accordion
                // Audit rows are immutable and never reordered, so the index is a
                // stable identity for the step list.
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                id={`audit-step-${index}`}
                className="kodi-audit__step"
                styled
                expanded={openSteps.includes(index)}
                handleToggle={() => toggleStep(index)}
                icon={<span className="kodi-audit__step-n">{index + 1}</span>}
                title={<span className="kodi-audit__step-name">{tool.name}</span>}
                actions={
                  // stopPropagation so copying doesn't also toggle the accordion.
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                  <span onClick={(event) => event.stopPropagation()}>
                    <CopyButton
                      content={serializedToolCalls[index]}
                      iconOnly
                      basic
                      aria-label={`Copy ${tool.name} tool call`}
                    />
                  </span>
                }
              >
                {tool.rationale && (
                  <p className="kodi-audit__rationale muted-text">{tool.rationale}</p>
                )}
                {!isNil(tool.input) && (
                  <pre className="kodi-audit__input">{JSON.stringify(tool.input, null, 2)}</pre>
                )}
              </Accordion>
            ))}
          </div>
        </div>
      )}

      {!isEmpty(skills) && (
        <div>
          <p className="kodi-audit__label">Skills used</p>
          <div className="kodi-audit__skills">
            {skills.map((skill) => (
              <Pill key={skill} xs>
                {skill}
              </Pill>
            ))}
          </div>
        </div>
      )}

      {!isNil(record.query) && (
        <div>
          <p className="kodi-audit__label">Prompt</p>
          <div className="kodi-audit__prompt">{record.query}</div>
        </div>
      )}

      {!isNil(record.final_response) && (
        <Accordion
          id="audit-response"
          className="kodi-audit__collapse"
          expanded={responseOpen}
          handleToggle={() => setResponseOpen((open) => !open)}
          title={
            <span className="kodi-audit__collapse-title">
              Response <span className="kodi-audit__collapse-note">· also shown in chat</span>
            </span>
          }
        >
          <pre className="kodi-audit__input">{record.final_response}</pre>
        </Accordion>
      )}
    </div>
  )
}
